import "leaflet/dist/leaflet.css";
import { CircleMarker, MapContainer, TileLayer } from "react-leaflet";
import css from "./ReportMap.module.css";

interface CountryEventData {
  countryId: string; // ISO alpha-2 code
  countryName: string;
  eventCount: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

// Function to get color based on event count using orange-to-red intensity gradient
const getColor = (count: number, minCount: number, maxCount: number) => {
  // Handle edge case where all values are the same
  if (maxCount === minCount) {
    return `hsl(25, 70%, 60%)`; // Default orange
  }

  // Normalize count based on actual data range (0 to 1)
  const normalizedCount = (count - minCount) / (maxCount - minCount);

  // Orange to red spectrum (25° to 0°) with increasing saturation and decreasing lightness
  const hue = 25 - normalizedCount * 25; // 25° (orange) to 0° (red)
  const saturation = 60 + normalizedCount * 30; // 60% to 90% (more vivid with higher intensity)
  const lightness = 65 - normalizedCount * 35; // 65% to 30% (darker with higher intensity)

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const ReportMap = ({
  countryEvents,
}: {
  countryEvents: CountryEventData[];
}) => {
  // Calculate min and max event counts from the actual data
  const eventCounts = countryEvents.map((country) => country.eventCount);
  const minCount = Math.min(...eventCounts);
  const maxCount = Math.max(...eventCounts);

  return (
    <div className={css["map-wrapper"]}>
      {/* Color Legend */}
      <div className={css.legend}>
        <span>Event Count:</span>
        <span className={css["legend-label"]}>{minCount}</span>
        <div className={css["legend-gradient"]} />
        <span className={css["legend-label"]}>{maxCount}</span>
      </div>

      {/* Map */}
      <MapContainer center={[20, 0]} zoom={2} className={css["map-container"]}>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" />

        {/* Render country markers declaratively */}
        {countryEvents.map((country) => {
          const coords = country.coordinates;
          if (!coords) return null;

          return (
            <CircleMarker
              key={country.countryId}
              center={[coords.latitude, coords.longitude]}
              radius={Math.max(8, country.eventCount * 3)}
              fillColor={getColor(country.eventCount, minCount, maxCount)}
              color="transparent"
              weight={0}
              fillOpacity={1}
              className={css["hover-only-marker"]}
              eventHandlers={{
                mouseover: (e) => {
                  e.target
                    .bindTooltip(
                      `${country.countryName}: ${country.eventCount} event${country.eventCount !== 1 ? "s" : ""}`,
                      { permanent: false, direction: "top" },
                    )
                    .openTooltip();
                },
              }}
            />
          );
        })}
      </MapContainer>
    </div>
  );
};

export default ReportMap;
